import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  X, UserPlus, Users, Loader2, Trash2, ChevronDown, Crown, Eye, Pencil
} from 'lucide-react';
import { shareAPI } from '../api';

export default function ShareWorkspaceModal({ workspaceId, workspaceName, onClose }) {
  const [shares, setShares] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [roleDropdown, setRoleDropdown] = useState(null);

  useEffect(() => { loadShares(); }, [workspaceId]);

  const loadShares = async () => {
    try {
      const res = await shareAPI.list(workspaceId);
      setOwner(res.data.owner);
      setShares(res.data.shares || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await shareAPI.add(workspaceId, email.trim(), role);
      setShares(prev => {
        const idx = prev.findIndex(s => s.email === res.data.email);
        if (idx >= 0) { const updated = [...prev]; updated[idx] = res.data; return updated; }
        return [res.data, ...prev];
      });
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to share');
    }
    finally { setSending(false); }
  };

  const handleRoleChange = async (shareId, newRole) => {
    try {
      await shareAPI.update(workspaceId, shareId, newRole);
      setShares(prev => prev.map(s => s.id === shareId ? { ...s, role: newRole } : s));
    } catch (e) { console.error(e); }
    setRoleDropdown(null);
  };

  const handleRemove = async (shareId) => {
    try {
      await shareAPI.remove(workspaceId, shareId);
      setShares(prev => prev.filter(s => s.id !== shareId));
    } catch (e) { console.error(e); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal-overlay">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="share-modal">
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#6590FF]" />
            <span className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Share "{workspaceName}"</span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-lg transition-colors" data-testid="close-share-modal"><X className="w-4 h-4 text-[#4B5563]" /></button>
        </div>

        <div className="p-5">
          <form onSubmit={handleShare} className="flex gap-2 mb-4">
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter email address" type="email" required className="flex-1 rounded-xl h-10" data-testid="share-email-input" />
            <select value={role} onChange={e => setRole(e.target.value)} className="h-10 px-3 text-xs font-medium border border-[#E5E7EB] rounded-xl bg-white text-[#4B5563] focus:outline-none focus:border-[#6590FF]" data-testid="share-role-select">
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
            <button type="submit" disabled={sending || !email.trim()} className="h-10 px-4 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-testid="share-submit-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Share
            </button>
          </form>
          {error && <p className="text-xs text-[#DC2626] mb-3 bg-[#FEE2E2] px-3 py-2 rounded-lg" data-testid="share-error">{error}</p>}

          <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
            <div className="bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B7280]">People with access ({(shares.length || 0) + (owner ? 1 : 0)})</p>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-[#F3F4F6]">
              {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#6590FF]" /></div>
              ) : (
                <>
                  {owner && <PersonRow person={owner} isOwner />}
                  {shares.map(s => (
                    <PersonRow key={s.id} person={s} roleDropdown={roleDropdown} setRoleDropdown={setRoleDropdown} onRoleChange={handleRoleChange} onRemove={handleRemove} />
                  ))}
                  {!owner && shares.length === 0 && <p className="text-xs text-[#9CA3AF] text-center py-6">Not shared with anyone yet</p>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonRow({ person, isOwner, roleDropdown, setRoleDropdown, onRoleChange, onRemove }) {
  const initials = (person.name || person.email || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const color = isOwner ? '#6590FF' : person.role === 'editor' ? '#059669' : '#6B7280';
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#F9FAFB] transition-colors group" data-testid={`share-person-${person.email}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: color }}>{initials}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[#111827] truncate">{person.name || person.email}</p>
        <p className="text-[10px] text-[#9CA3AF] truncate">{person.email}</p>
      </div>
      {isOwner ? (
        <span className="flex items-center gap-1 text-[10px] font-bold text-[#6590FF] bg-[#6590FF]/5 px-2 py-1 rounded-full"><Crown className="w-3 h-3" /> Owner</span>
      ) : (
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <button onClick={() => setRoleDropdown(roleDropdown === person.id ? null : person.id)}
              className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border border-[#E5E7EB] hover:border-[#D1D5DB] transition-colors"
              data-testid={`role-btn-${person.id}`}>
              {person.role === 'editor' ? <Pencil className="w-2.5 h-2.5 text-[#059669]" /> : <Eye className="w-2.5 h-2.5 text-[#6B7280]" />}
              <span className={person.role === 'editor' ? 'text-[#059669]' : 'text-[#6B7280]'}>{person.role === 'editor' ? 'Editor' : 'Viewer'}</span>
              <ChevronDown className="w-2.5 h-2.5 text-[#9CA3AF]" />
            </button>
            {roleDropdown === person.id && (
              <div className="absolute right-0 top-7 w-28 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-10">
                <button onClick={() => onRoleChange(person.id, 'viewer')} className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-1.5 hover:bg-[#F3F4F6] ${person.role === 'viewer' ? 'text-[#6590FF] font-bold' : 'text-[#4B5563]'}`}>
                  <Eye className="w-3 h-3" /> Viewer
                </button>
                <button onClick={() => onRoleChange(person.id, 'editor')} className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-1.5 hover:bg-[#F3F4F6] ${person.role === 'editor' ? 'text-[#6590FF] font-bold' : 'text-[#4B5563]'}`}>
                  <Pencil className="w-3 h-3" /> Editor
                </button>
              </div>
            )}
          </div>
          <button onClick={() => onRemove(person.id)}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#FEE2E2] rounded-lg transition-all" data-testid={`remove-share-${person.id}`}>
            <Trash2 className="w-3 h-3 text-[#DC2626]" />
          </button>
        </div>
      )}
    </div>
  );
}
